// Cloud Functions-based OpenAI utilities
// This replaces direct API calls with secure Cloud Functions calls

import { auth } from '../config/firebase-config';

// Anonymous auth is optional for these proxies. Keep it disabled by default
// because the deployed functions already accept unauthenticated requests.
let _anonymousAuthFailed = false;

function shouldAttemptAnonymousAuth() {
  return process.env.REACT_APP_ENABLE_ANONYMOUS_AUTH === 'true';
}

/**
 * Get the Firebase Auth token for authenticated requests
 * Returns null when no Firebase user is available because these endpoints
 * can operate without client auth.
 */
async function getAuthToken() {
  let user = auth.currentUser;

  if (!user && shouldAttemptAnonymousAuth() && !_anonymousAuthFailed) {
    try {
      const { signInAnonymously } = await import('firebase/auth');
      const userCredential = await signInAnonymously(auth);
      user = userCredential.user;
    } catch (error) {
      _anonymousAuthFailed = true;
      return null;
    }
  }

  if (!user) {
    return null;
  }

  return await user.getIdToken();
}

/**
 * Get the Cloud Function URL
 */
function getFunctionUrl(functionName) {
  const isDevelopment = process.env.NODE_ENV === 'development';
  if (isDevelopment && process.env.REACT_APP_FIREBASE_EMULATOR_HOST) {
    return `http://${process.env.REACT_APP_FIREBASE_EMULATOR_HOST}/${functionName}`;
  }
  
  const projectId = 'nur-al-huda';
  const region = 'us-central1';
  return `https://${region}-${projectId}.cloudfunctions.net/${functionName}`;
}

/**
 * Make a secure API call to Firebase Cloud Function
 */
async function callSecureFunction(functionName, data) {
  const token = await getAuthToken();
  const url = getFunctionUrl(functionName);
  
  const headers = {
    'Content-Type': 'application/json',
  };
  
  // Only add Authorization header if we have a token
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  
  const response = await fetch(url, {
    method: 'POST',
    headers,
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const error = await response.json();
    const err = new Error(error.error || `HTTP error! status: ${response.status}`);
    err.status = response.status;
    err.code = error.code;
    throw err;
  }

  return await response.json();
}

let currentStream = null;

// Helper function to check if error is an authentication error (401)
function isAuthenticationError(error) {
  return error?.status === 401 || 
         error?.code === 'invalid_api_key' ||
         error?.message?.includes('invalid_api_key') ||
         error?.message?.includes('Incorrect API key');
}

/**
 * Create a thread
 */
export async function createThread(assistantTitle, resourceId = null) {
  console.log('Creating thread via Cloud Functions:', { assistantTitle, resourceId });
  
  try {
    const result = await callSecureFunction('openaiProxy', {
      action: 'createThread',
      assistantTitle,
      resourceId,
    });
    
    console.log('Thread created:', result.id);
    return result;
  } catch (error) {
    console.error('Error creating thread:', error);
    throw error;
  }
}

/**
 * Create a message in a thread
 */
export async function createMessage(threadId, role, content, assistantTitle, resourceId = null, fileIds = []) {
  console.log('Creating message via Cloud Functions:', { threadId, role, assistantTitle, resourceId });
  
  try {
    const payload = {
      action: 'createMessage',
      assistantTitle,
      resourceId,
      threadId,
      role,
      content,
    };

    // file_ids is deprecated for threads.messages.create in newer SDK/API.
    // Only include it when explicitly provided to preserve legacy compatibility.
    if (Array.isArray(fileIds) && fileIds.length > 0) {
      payload.file_ids = fileIds;
    }

    const result = await callSecureFunction('openaiProxy', payload);
    
    console.log('Message created:', result.id);
    return result;
  } catch (error) {
    console.error('Error creating message:', error);
    throw error;
  }
}

/**
 * Create a non-streaming response via Responses API
 */
export async function createResponse(requestBody, assistantTitle, resourceId = null) {
  console.log('Creating response via Cloud Functions:', {
    assistantTitle,
    resourceId,
    hasPreviousResponseId: Boolean(requestBody?.previous_response_id),
  });

  try {
    const result = await callSecureFunction('openaiProxy', {
      action: 'createResponse',
      assistantTitle,
      resourceId,
      ...requestBody,
    });

    console.log('Response created:', result.id);
    return result;
  } catch (error) {
    console.error('Error creating response:', error);
    throw error;
  }
}

/**
 * Create a message via Anthropic Claude API
 */
export async function createClaudeResponse(requestBody) {
  try {
    const result = await callSecureFunction('anthropicProxy', {
      action: 'createMessage',
      ...requestBody,
    });
    return result;
  } catch (error) {
    console.error('Error creating Claude response:', error);
    throw error;
  }
}

/**
 * Create a run with streaming
 */
export function createRun(threadId, assistantId, handleMessage, handleError, assistantTitle, resourceId = null) {
  console.log('Creating run via Cloud Functions:', { threadId, assistantId, assistantTitle, resourceId });
  
  if (currentStream) {
    console.warn("A stream is already active, aborting new run initiation.");
    return;
  }

  const runStream = () => {
    return new Promise((resolve, reject) => {
      (async () => {
        try {
          const token = await getAuthToken();
          const url = getFunctionUrl('openaiStreamProxy');
          
          const headers = {
            'Content-Type': 'application/json',
          };
          if (token) {
            headers['Authorization'] = `Bearer ${token}`;
          }
          
          const response = await fetch(url, {
            method: 'POST',
            headers,
            body: JSON.stringify({
              action: 'createRunStream',
              assistantTitle,
              resourceId,
              threadId,
              assistant_id: assistantId,
            }),
          });

          if (!response.ok) {
            const errorData = await response.json();
            const error = new Error(errorData.error || `HTTP error! status: ${response.status}`);
            error.status = response.status;
            error.code = errorData.code;
            throw error;
          }

          const reader = response.body.getReader();
          const decoder = new TextDecoder();
          let buffer = ''; // Buffer for incomplete SSE lines
          
          currentStream = { reader, active: true };

          while (currentStream.active) {
            const { done, value } = await reader.read();
            
            if (done) {
              console.log('Stream ended successfully');
              handleMessage({ sender: "assistant", text: "END_TOKEN" });
              currentStream = null;
              resolve();
              break;
            }

            // Append new chunk to buffer
            buffer += decoder.decode(value, { stream: true });
            
            // Process complete SSE lines
            let newlineIndex;
            while ((newlineIndex = buffer.indexOf('\n')) !== -1) {
              const line = buffer.substring(0, newlineIndex);
              buffer = buffer.substring(newlineIndex + 1);
              
              const trimmedLine = line.trim();
              
              // Skip empty lines (SSE event delimiter)
              if (!trimmedLine) {
                continue;
              }
              
              if (trimmedLine.startsWith('data: ')) {
                const data = trimmedLine.slice(6); // Get data after "data: " prefix
                
                if (data === '[DONE]') {
                  console.log('Stream completed');
                  handleMessage({ sender: "assistant", text: "END_TOKEN" });
                  currentStream = null;
                  resolve();
                  return;
                }
                
                if (data === '[ERROR]') {
                  console.error('Stream error indicator received');
                  continue;
                }
                
                if (!data) {
                  continue; // Skip empty data lines
                }
                
                try {
                  const parsed = JSON.parse(data);
                  
                  // Handle error events
                  if (parsed.error) {
                    const error = new Error(parsed.error);
                    error.status = parsed.status;
                    error.code = parsed.code;
                    throw error;
                  }
                  
                  // Handle text delta events
                  if (parsed.event === 'thread.message.delta') {
                    const textDelta = (parsed.data?.delta?.content || [])
                      .filter((item) => item?.type === 'text' && item.text?.value)
                      .map((item) => item.text.value)
                      .join('');

                    if (textDelta) {
                      handleMessage({ sender: "assistant", text: textDelta });
                    }
                  }
                } catch (parseError) {
                  // Log parse errors but don't break the stream
                  if (data && data !== '{}' && data !== '[DONE]' && data !== '[ERROR]') {
                    // Check if this might be incomplete JSON
                    const mightBeIncomplete = 
                      parseError instanceof SyntaxError &&
                      (parseError.message.includes('Unterminated') ||
                       parseError.message.includes('Unexpected end') ||
                       data.endsWith('\\') ||
                       (data.match(/"/g) || []).length % 2 !== 0);
                    
                    if (mightBeIncomplete) {
                      // Put it back in buffer and wait for more data
                      buffer = `data: ${data}\n${buffer}`;
                      break; // Exit inner loop to wait for more chunks
                    } else {
                      // Log but continue - don't break streaming
                      console.warn('Error parsing SSE data (skipping):', parseError.message);
                      console.debug('Problematic data (first 200 chars):', data.substring(0, 200));
                    }
                  }
                }
              }
            }
          }
        } catch (error) {
          console.error("Stream error:", error);
          currentStream = null;
          
          if (isAuthenticationError(error)) {
            console.warn('Authentication error detected');
          }
          
          reject(error);
        }
      })();
    });
  };

  runStream().catch(error => {
    if (handleError) {
      handleError(error);
    }
  });
}

/**
 * Cancel the current run
 */
export function cancelRun(threadId, runId, assistantTitle, resourceId = null) {
  console.log('Canceling run via Cloud Functions:', { threadId, runId });
  
  if (currentStream) {
    currentStream.active = false;
    currentStream = null;
    console.log('Stream aborted locally');
  }

  return callSecureFunction('openaiProxy', {
    action: 'cancelRun',
    assistantTitle,
    resourceId,
    threadId,
    runId,
  });
}

/**
 * List messages in a thread
 */
export async function listMessages(threadId, assistantTitle, resourceId = null, limit = 20) {
  console.log('Listing messages via Cloud Functions:', { threadId, limit });
  
  try {
    const result = await callSecureFunction('openaiProxy', {
      action: 'listMessages',
      assistantTitle,
      resourceId,
      threadId,
      limit,
      order: 'desc',
    });
    
    return result;
  } catch (error) {
    console.error('Error listing messages:', error);
    throw error;
  }
}

/**
 * Generate TTS (Text-to-Speech) audio
 */
export async function generateTTS(text, assistantTitle, resourceId = null, options = {}) {
  console.log('Generating TTS via Cloud Functions:', { assistantTitle, textLength: text.length });
  
  try {
    const token = await getAuthToken();
    const url = getFunctionUrl('openaiTTS');
    
    const headers = {
      'Content-Type': 'application/json',
    };
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    
    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        assistantTitle,
        resourceId,
        model: options.model || 'tts-1',
        voice: options.voice || 'alloy',
        input: text,
      }),
    });

    if (!response.ok) {
      const error = await response.json();
      const err = new Error(error.error || `HTTP error! status: ${response.status}`);
      err.status = response.status;
      err.code = error.code;
      throw err;
    }

    // Return the audio blob
    const blob = await response.blob();
    return blob;
  } catch (error) {
    console.error('Error generating TTS:', error);
    throw error;
  }
}

/**
 * Transcribe audio using Whisper
 */
export async function transcribeAudio(file, assistantTitle, resourceId = null, options = {}) {
  console.log('Transcribing audio via Cloud Functions:', { assistantTitle });
  
  try {
    // Convert file to base64
    const reader = new FileReader();
    const fileData = await new Promise((resolve, reject) => {
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });

    const token = await getAuthToken();
    const url = getFunctionUrl('openaiWhisper');
    
    const headers = {
      'Content-Type': 'application/json',
    };
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    
    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        assistantTitle,
        resourceId,
        fileData,
        fileName: file.name || 'audio.webm',
        fileType: file.type || 'audio/webm',
        model: options.model || 'whisper-1',
      }),
    });

    if (!response.ok) {
      const error = await response.json();
      const err = new Error(error.error || `HTTP error! status: ${response.status}`);
      err.status = response.status;
      err.code = error.code;
      throw err;
    }

    const result = await response.json();
    return result;
  } catch (error) {
    console.error('Error transcribing audio:', error);
    throw error;
  }
}

/**
 * Create a streaming Responses API call
 */
export function createResponsesStream(requestBody, assistantTitle, resourceId = null, callbacks = {}) {
  const {
    onChunk = () => {},
    onStatus = () => {},
    onFileSearch = () => {},
    onImageGeneration = () => {},
    onImageGenerated = () => {},
    onReasoning = () => {},
    onComplete = () => {}
  } = callbacks;

  return new Promise((resolve, reject) => {
    (async () => {
      try {
        let completeCalled = false;
        const callOnComplete = (arg) => {
          if (completeCalled) return;
          completeCalled = true;
          onComplete(arg);
        };

        const token = await getAuthToken();
        const url = getFunctionUrl('openaiResponsesStream');
        
        const headers = {
          'Accept': 'text/event-stream',
          'Content-Type': 'application/json',
        };
        
        // Only add Authorization header if we have a token
        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
        }
        
        const response = await fetch(url, {
          method: 'POST',
          headers,
          body: JSON.stringify({
            assistantTitle,
            resourceId,
            ...requestBody,
          }),
        });

        if (!response.ok) {
          let errorData;
          let errorMessage = `HTTP error! status: ${response.status}`;
          
          // Try to parse error response, but handle cases where it might not be JSON
          try {
            const contentType = response.headers.get('content-type');
            if (contentType && contentType.includes('application/json')) {
              errorData = await response.json();
              errorMessage = errorData.error || errorMessage;
            } else {
              // For 502 errors and other gateway errors, might be HTML or empty
              const text = await response.text();
              if (text) {
                errorMessage = `HTTP ${response.status}: ${text.substring(0, 200)}`;
              }
            }
          } catch (parseError) {
            // If we can't parse the error, use status-based message
            if (response.status === 502) {
              errorMessage = 'Bad Gateway: The Cloud Function may have timed out or crashed. This is often temporary.';
            } else if (response.status === 504) {
              errorMessage = 'Gateway Timeout: The request took too long to process.';
            } else {
              errorMessage = `HTTP error! status: ${response.status}`;
            }
          }
          
          const error = new Error(errorMessage);
          error.status = response.status;
          error.code = errorData?.code;
          // Mark 502 and 504 as retryable
          error.retryable = response.status === 502 || response.status === 504;
          throw error;
        }

        if (!response.body) {
          throw new Error('Streaming response body is unavailable. The HTTP response was likely buffered before reaching the browser.');
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = ''; // Buffer for incomplete SSE lines
        const outputItemsWithDelta = new Set();
        
        // Helper function to process events
        function processEvent(parsed) {
          const eventType = parsed.type;
          console.log('[sse] event:', eventType);

          if (
            eventType === 'response.reasoning_summary_part.added' ||
            eventType === 'response.reasoning.in_progress' ||
            eventType === 'response.reasoning_summary.in_progress'
          ) {
            onReasoning('thinking', parsed);
            return;
          }

          if (eventType === 'response.reasoning_summary_text.delta') {
            if (parsed.delta) {
              onReasoning('thinking_delta', parsed.delta);
            }
            return;
          }

          if (
            eventType === 'response.reasoning_summary_text.done' ||
            eventType === 'response.reasoning_summary_part.done' ||
            eventType === 'response.reasoning.done' ||
            eventType === 'response.reasoning_summary.done'
          ) {
            onReasoning('thinking_done', parsed);
            return;
          }
          
          switch (eventType) {
            case 'response.created':
              onStatus('created', parsed.response);
              break;
            case 'response.in_progress':
              onStatus('in_progress', parsed.response);
              break;
            case 'response.completed':
              onStatus('completed', parsed.response);
              callOnComplete(parsed.response);
              break;
            case 'response.output_text.delta':
              if (parsed.delta) {
                const outputItemKey = parsed.item_id || `${parsed.output_index}:${parsed.content_index}`;
                outputItemsWithDelta.add(outputItemKey);
                onChunk(parsed.delta);
              }
              break;
            case 'response.output_text.done':
              if (parsed.text) {
                const outputItemKey = parsed.item_id || `${parsed.output_index}:${parsed.content_index}`;
                if (!outputItemsWithDelta.has(outputItemKey)) {
                  onChunk(parsed.text);
                }
              }
              break;
            case 'response.output_item.done':
              if (parsed.item?.type === 'image_generation_call') {
                onImageGenerated(parsed.item);
                onImageGeneration('completed', parsed.item);
              }
              break;
            case 'response.image_generation_call.in_progress':
              onImageGeneration('in_progress', parsed);
              break;
            case 'response.image_generation_call.completed':
              onImageGeneration('completed', parsed);
              break;
            case 'response.image_generation_call.failed':
              onImageGeneration('failed', parsed);
              break;
            case 'response.file_search_call.completed':
              onFileSearch('completed', parsed);
              break;
            default:
              // Handle other event types as needed
              break;
          }
        }
        
        while (true) {
          const { done, value } = await reader.read();
          
          if (done) {
            callOnComplete();
            resolve();
            break;
          }

          // Append new chunk to buffer
          buffer += decoder.decode(value, { stream: true });
          
          // Process complete JSON Lines (one JSON object per line)
          let newlineIndex;
          while ((newlineIndex = buffer.indexOf('\n')) !== -1) {
            const line = buffer.substring(0, newlineIndex);
            buffer = buffer.substring(newlineIndex + 1);

            const trimmedLine = line.trim();
            if (!trimmedLine) continue;

            try {
              const parsed = JSON.parse(trimmedLine);

              if (parsed.error) {
                const error = new Error(parsed.error);
                error.status = parsed.status;
                error.code = parsed.code;
                throw error;
              }

              processEvent(parsed);
            } catch (parseError) {
              if (parseError instanceof Error && parseError.status) {
                throw parseError;
              }
              // Incomplete JSON split across network chunks — put back and wait
              if (parseError instanceof SyntaxError) {
                const msg = parseError.message;
                if (msg.includes('Unterminated') || msg.includes('Unexpected end')) {
                  buffer = line + '\n' + buffer;
                  break;
                }
              }
              // Malformed line — log and skip, don't break the stream
              if (trimmedLine.length > 0) {
                console.warn('Skipping malformed JSON line:', trimmedLine.substring(0, 100));
              }
            }
          }
        }
      } catch (error) {
        console.error('Responses Stream Error:', error);
        // Let the caller decide whether to surface the error or retry first.
        reject(error);
      }
    })();
  });
}

/**
 * Upload a file to OpenAI
 */
export async function uploadFile(file, assistantTitle, resourceId = null, purpose = 'assistants') {
  console.log('Uploading file via Cloud Functions:', { assistantTitle, fileName: file.name });
  
  try {
    // Convert file to base64
    const reader = new FileReader();
    const fileData = await new Promise((resolve, reject) => {
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });

    const token = await getAuthToken();
    const url = getFunctionUrl('openaiUploadFile');
    
    const headers = {
      'Content-Type': 'application/json',
    };
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    
    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        assistantTitle,
        resourceId,
        fileData,
        fileName: file.name || 'file',
        fileType: file.type || 'application/octet-stream',
        purpose,
      }),
    });

    if (!response.ok) {
      const error = await response.json();
      const err = new Error(error.error || `HTTP error! status: ${response.status}`);
      err.status = response.status;
      err.code = error.code;
      throw err;
    }

    const result = await response.json();
    return result;
  } catch (error) {
    console.error('Error uploading file:', error);
    throw error;
  }
}
